import { yupResolver } from "@hookform/resolvers";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { UserMadFullFragment } from "../../graphql/fragments";
import { GET_USER } from "../../hooks/useGetUser";
import useInsertUser from "../../hooks/useInsertUser";
import useViewer from "../../hooks/useViewer";
import useTranslations from "../../hooks/useTranslations";
import { CloseIcon, EditIcon, SaveIcon } from "../icons";
import { Button, H4, Textarea } from "../wombat-ui";

type CollaboratorNotesSectionProps = {
  user: UserMadFullFragment;
};

type CollaboratorNotesInput = {
  id: string;
  notes: string;
};

const CollaboratorNotesSection: React.FC<CollaboratorNotesSectionProps> = ({
  user,
}) => {
  const t = useTranslations();
  const viewer = useViewer();

  const [editingNotes, setEditingNotes] = useState<boolean>(false);

  const CollaboratorNotesFormSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          notes: yup.string(),
        })
        .required(),
    [t]
  );

  const {
    register,
    handleSubmit,
    setValue,
    errors,
    watch,
    setError,
  } = useForm<CollaboratorNotesInput>({
    resolver: yupResolver(CollaboratorNotesFormSchema),
    defaultValues: {
      notes: user.notes,
    },
  });

  const watchNotes = watch("notes");

  const [updateCollaboratorNotes, { loading }] = useInsertUser({
    refetchQueries: [{ query: GET_USER, variables: { id: user.id } }],
  });

  const onSubmit = useCallback((data) => {
    updateCollaboratorNotes({
      variables: {
        input: { ...data, id: user.id },
      },
    })
      .then(() => {
        setEditingNotes(false);
      })
      .catch((error) => {
        const field = error.graphQLErrors?.[0]?.field;
        const errorMessage = t(
          `forms.errors.${error.graphQLErrors?.[0]?.details?.[0]?.error}`,
          { value: error.graphQLErrors?.[0]?.details?.[0]?.value }
        ) as string;
        setError(field, {
          message: errorMessage,
        });
      });
  }, []);

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <H4>
            {t("collaborator.notes")}
          </H4>
          <p className="text-xs font-medium text-gray-400">
            {t("collaborator.notesDescription", {
              name: user.name,
            })}
          </p>
        </div>
        <div>
          <Button
            id="saveNotes"
            className={`mx-1 ${!editingNotes && "hidden"}`}
            type="submit"
            variant="ghost"
            loading={loading}
            form="updateCollaboratorNotesForm"
            icon={SaveIcon}
          />
          <Button
            id="closeNotes"
            className={!editingNotes && "hidden"}
            variant="ghost"
            form="updateCollaboratorNotesForm"
            onClick={() => {
              setEditingNotes(false);
              setValue("notes", user.notes);
            }}
            icon={CloseIcon}
          />
          <Button
            id="editNotes"
            className={editingNotes && "hidden"}
            variant="ghost"
            type="button"
            onClick={() => setEditingNotes(true)}
            disabled={!viewer.isCapableOfUpdatingUserNotes}
            icon={EditIcon}
          />
        </div>
      </div>
      <div>
        <form
          className="mt-3 mb-4 space-y-4"
          id="updateCollaboratorNotesForm"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Textarea
            id="notes"
            name="notes"
            disabled={!editingNotes}
            ref={register({ required: false })}
            label=""
            placeholder={t("forms.fields.notes") as string}
          />
        </form>
      </div>
    </>
  );
};

export default CollaboratorNotesSection;
